import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuditOutlined,
  BookOutlined,
  CloudUploadOutlined,
  DashboardOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PictureOutlined,
  ReadOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  ConfigProvider,
  Descriptions,
  Drawer,
  Form,
  Input,
  Layout,
  Menu,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { MenuProps, UploadProps } from 'antd';
import dayjs from 'dayjs';
import { api, download, json, type AdminUser } from './api';
import { batchStateIds } from './batch-state';
import { navigateAdmin, useAdminRoute, useUnsavedEditor } from './admin-navigation';
import { EditorPage, EditorWorkspace } from './editor-workspace';
import {
  ContentEditor,
  contentDisplayName,
  createContentDefault,
  prepareContentForEditor,
  resourceKeyFor,
  sanitizeContentForSave,
  type EditableContentType,
} from './content-editors';
const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
type ContentType = EditableContentType;
type ContentRow = {
  id: string;
  resource_key: string;
  state: 'draft' | 'published' | 'offline';
  data: unknown;
  sort: number;
  revision: number;
  updated_at: string;
};
type VersionRow = { id: string; version: number; state: string; created_at: string };

type StatusPresentation = { label: string; color: string };

const releaseStatuses: Record<string, StatusPresentation> = {
  queued: { label: '待发布', color: 'gold' },
  building: { label: '构建中', color: 'blue' },
  deploying: { label: '部署中', color: 'cyan' },
  active: { label: '已发布', color: 'green' },
  superseded: { label: '已替换', color: 'default' },
  failed: { label: '发布失败', color: 'red' },
};

const releaseJobStatuses: Record<string, StatusPresentation> = {
  queued: { label: '排队中', color: 'gold' },
  building: { label: '构建中', color: 'blue' },
  deploying: { label: '部署中', color: 'cyan' },
  active: { label: '已完成', color: 'green' },
  failed: { label: '执行失败', color: 'red' },
};

function statusTag(value: unknown, statuses: Record<string, StatusPresentation>) {
  if (value === null || value === undefined || value === '') return <Tag>-</Tag>;
  const status = String(value);
  const presentation = statuses[status];
  return <Tag color={presentation?.color}>{presentation?.label ?? status}</Tag>;
}

function contentPagePath(type: ContentType, key: string) {
  if (type === 'course') return `/courses/${key}/`;
  if (type === 'material') return `/materials/${key}/`;
  if (type === 'case') return `/cases/${key}/`;
  if (type === 'about') return '/about/';
  if (type === 'home' || type === 'teacher') return '/';
  return '';
}

function embeddedPreviewUrl(url: string, type: ContentType, anchor?: string, refreshKey?: number) {
  const preview = new URL(url);
  preview.searchParams.set('previewFrame', '1');
  preview.searchParams.set('previewOrigin', window.location.origin);
  if (refreshKey !== undefined) preview.searchParams.set('draftVersion', String(refreshKey));
  if (anchor) preview.hash = anchor;
  else if (type === 'teacher') preview.hash = 'sec-teachers';
  return preview.toString();
}

type HomeModule = {
  key: string;
  title: string;
  description: string;
  fieldKeys?: string[];
  anchor?: string;
  navigateTo?: 'teacher' | 'course' | 'case';
  editorType?: 'teachers';
};

const homeModules: HomeModule[] = [
  {
    key: 'hero',
    title: '首屏',
    description:
      '两行主标题、高亮文字、说明、按钮和信任提示；合作伙伴标识与右侧测评演示为固定样式。',
    fieldKeys: [
      'hero.title',
      'hero.highlightText',
      'hero.description',
      'hero.primaryCta',
      'hero.primaryTarget',
      'hero.secondaryCta',
      'hero.secondaryTarget',
      'hero.trust',
    ],
    anchor: 'sec-hero',
  },
  {
    key: 'stats',
    title: '核心数据',
    description: '首页四项核心数据指标。',
    fieldKeys: ['stats'],
    anchor: 'sec-stats',
  },
  {
    key: 'ai',
    title: 'AI 产品',
    description: 'AI 产品卡片、产品详解及写作、口语卖点。',
    fieldKeys: [
      'aiTitle',
      'aiDescription',
      'aiProducts',
      'productDetail',
      'writingFeatures',
      'speakingFeatures',
    ],
    anchor: 'sec-ai',
  },
  {
    key: 'cases',
    title: '首页案例精选',
    description: '首页自动展示案例详情页列表中的前三条。',
    navigateTo: 'case',
    anchor: 'sec-cases',
  },
  {
    key: 'chat',
    title: '学员群聊墙',
    description: '群聊截图、说明和好评数据。',
    fieldKeys: ['chatWall'],
    anchor: 'sec-chat',
  },
  {
    key: 'courses',
    title: '首页课程推荐',
    description: '首页自动展示课程详情页列表中的前三条。',
    navigateTo: 'course',
    anchor: 'sec-course',
  },
  {
    key: 'teachers',
    title: '首页师资',
    description: '领衔老师和普通老师展示卡片，可在当前模块直接编辑。',
    editorType: 'teachers',
    anchor: 'sec-teachers',
  },
  {
    key: 'process',
    title: '服务流程',
    description: '报名后的服务步骤与每一步内容。',
    fieldKeys: ['process'],
    anchor: 'sec-process',
  },
  {
    key: 'trust',
    title: '品牌信任',
    description: '品牌介绍、发展历程、奖项与证书。',
    fieldKeys: ['brandTrust'],
    anchor: 'sec-brand-trust',
  },
  {
    key: 'faq',
    title: '常见问题',
    description: 'FAQ 内容、提示和两个操作按钮。',
    fieldKeys: ['faqIntro', 'faqs'],
    anchor: 'sec-faq',
  },
  {
    key: 'lead',
    title: '底部留资',
    description: '底部表单、提示文案和常驻二维码。',
    fieldKeys: ['lead', 'qrCodes.bottom'],
    anchor: 'sec-lead',
  },
  {
    key: 'activity',
    title: '底部活动条',
    description: '页面底部活动提醒、按钮和点击动作。',
    fieldKeys: ['activity'],
  },
];
const homeQrModule: HomeModule = {
  key: 'homeQr',
  title: '首页兜底二维码',
  description: '首页按钮未指定其他二维码时，统一使用这里的二维码。',
  fieldKeys: ['qrCodes.consult.image'],
};
const contentLabels: Record<ContentType, string> = {
  course: '课程详情页',
  case: '案例详情页',
  teacher: '首页师资',
  material: '资料详情页',
  home: '首页内容',
  about: '企业介绍页',
  navigation: '顶部导航',
  footer: '页脚栏目与链接',
  brand: '品牌与通用设置',
  qr: '历史二维码码位',
  courseSettings: '课程兜底二维码',
  materialSettings: '资料兜底二维码',
  caseSettings: '案例兜底二维码',
  teacherSettings: '老师兜底二维码',
};
const contentDescriptions: Partial<Record<ContentType, string>> = {
  home: '配置官网首页各区块、AI 产品、按钮和二维码。',
  teacher: '配置首页师资展示卡片；老师当前没有独立详情页。',
  course: '每条内容对应一个可访问的课程详情页。',
  case: '每条内容对应一个可访问的学员案例详情页。',
  material: '每条内容对应一个可访问的备考资料详情页。',
  about: '配置官网“企业介绍”页面。',
  navigation: '配置全站顶部导航的名称、链接、显示状态和排序。',
  footer: '配置页脚中的栏目和链接；公司介绍、备案与法律信息在“品牌与通用设置”中维护。',
  brand: '配置全站品牌、公司备案、提交反馈、通用弹窗和默认二维码。',
};
const menuItems: MenuProps['items'] = [
  { key: 'dashboard', icon: <DashboardOutlined />, label: '概览' },
  {
    key: 'content',
    icon: <ReadOutlined />,
    label: '内容管理',
    children: [
      { key: 'home', label: '首页内容' },
      { key: 'course', label: '课程详情页' },
      { key: 'case', label: '案例详情页' },
      { key: 'material', label: '资料详情页' },
      { key: 'about', label: '企业介绍页' },
    ],
  },
  {
    key: 'operation',
    icon: <BookOutlined />,
    label: '运营管理',
    children: [{ key: 'leads', label: '线索' }],
  },
  {
    key: 'global',
    icon: <SettingOutlined />,
    label: '全局设置',
    children: [
      { key: 'brand', label: '品牌与通用设置' },
      { key: 'navigation', label: '顶部导航' },
      { key: 'footer', label: '页脚栏目与链接' },
    ],
  },
  {
    key: 'system',
    icon: <SettingOutlined />,
    label: '系统管理',
    children: [
      { key: 'assets', label: '图片处理工具（高级）' },
      { key: 'accounts', label: '账号权限' },
      { key: 'audit', label: '操作日志' },
      { key: 'releases', label: '发布任务与版本' },
    ],
  },
];
const permissionOptions = [
  ...Object.entries(contentLabels).map(([value, label]) => ({ value, label })),
  { value: 'leads', label: '线索' },
  { value: 'assets', label: '图片处理工具（高级）' },
  { value: 'audit', label: '操作日志' },
  { value: 'releases', label: '发布任务与版本' },
];
const permissionLabels = new Map([
  ['*', '全部权限'],
  ...permissionOptions.map(({ value, label }) => [value, label] as const),
]);

export function permissionDisplayName(permission: string) {
  return permissionLabels.get(permission) ?? '其他权限';
}

export function leadSourceDisplayName(source: string) {
  const labels: Record<string, string> = {
    'home-lead': '官网首页（底部留资）',
    test: '测试提交',
    'qa-smoke': '接口测试',
  };
  return labels[source] ?? '其他来源';
}

function menusFor(user: AdminUser): MenuProps['items'] {
  if (user.isSuper || user.permissions.includes('*')) return menuItems;
  const allowed = new Set(['dashboard', ...user.permissions]);
  return (menuItems ?? [])
    .map((item) => {
      if (!item) return null;
      if ('children' in item && Array.isArray(item.children)) {
        const children = item.children.filter(
          (child) =>
            child &&
            'key' in child &&
            (allowed.has(String(child.key)) ||
              (String(child.key) === 'home' && allowed.has('teacher'))),
        );
        return children.length ? { ...item, children } : null;
      }
      return 'key' in item && allowed.has(String(item.key)) ? item : null;
    })
    .filter(Boolean) as MenuProps['items'];
}

function Login({ onLogin }: { onLogin: (user: AdminUser) => void }) {
  const [loading, setLoading] = useState(false);
  const submit = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const result = await api<{ csrfToken: string; user: AdminUser }>(
        '/auth/login',
        json('POST', values),
      );
      localStorage.setItem('luobo-csrf', result.csrfToken);
      onLogin(result.user);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="login-page">
      <Card className="login-card">
        <div className="login-logo">🥕</div>
        <Title level={3}>萝卜雅思内容管理后台</Title>
        <Text type="secondary">使用管理员账号登录</Text>
        <Form layout="vertical" onFinish={submit} initialValues={{ username: 'admin' }}>
          <Form.Item label="账号" name="username" rules={[{ required: true }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item label="密码" name="password" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Button htmlType="submit" type="primary" block loading={loading}>
            登录
          </Button>
        </Form>
      </Card>
    </div>
  );
}

function PasswordReset({ user, onDone }: { user: AdminUser; onDone: () => void }) {
  return (
    <Modal open closable={false} footer={null} title="首次登录必须修改密码">
      <Alert
        type="warning"
        showIcon
        title={`账号 ${user.username} 使用的是初始化密码，请先更新密码。`}
      />
      <Form
        layout="vertical"
        onFinish={async (values) => {
          await api('/auth/password', json('POST', values));
          onDone();
        }}
      >
        <Form.Item label="当前密码" name="currentPassword" rules={[{ required: true }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item
          label="新密码（至少 12 位）"
          name="newPassword"
          rules={[{ required: true, min: 12 }]}
        >
          <Input.Password />
        </Form.Item>
        <Button type="primary" htmlType="submit">
          修改密码
        </Button>
      </Form>
    </Modal>
  );
}

function Dashboard() {
  const [cards, setCards] = useState({ course: 0, case: 0, material: 0, teacher: 0 });
  useEffect(() => {
    Promise.all(Object.keys(cards).map((key) => api<ContentRow[]>(`/content/${key}`)))
      .then((rows) =>
        setCards({
          course: rows[0]?.length ?? 0,
          case: rows[1]?.length ?? 0,
          material: rows[2]?.length ?? 0,
          teacher: rows[3]?.length ?? 0,
        }),
      )
      .catch(() => undefined);
  }, []);
  return (
    <>
      <Title level={2}>内容概览</Title>
      <div className="stats-row">
        <Card>
          <Statistic title="课程" value={cards.course} />
        </Card>
        <Card>
          <Statistic title="案例" value={cards.case} />
        </Card>
        <Card>
          <Statistic title="资料" value={cards.material} />
        </Card>
        <Card>
          <Statistic title="师资" value={cards.teacher} />
        </Card>
      </div>
      <Alert
        showIcon
        type="info"
        title="保存和上下架只更新后台；检查预览后，请到“发布任务与版本”发布整站，官网才会更新。"
      />
    </>
  );
}

function ModuleQrSettingsButton({
  type,
  label,
  title,
  fieldKeys,
  description = '此模块的二维码弹窗默认使用这里上传的图片。弹窗标题和说明统一在“全局设置”中维护。',
}: {
  type: ContentType;
  label: string;
  title?: string;
  fieldKeys?: string[];
  description?: string;
}) {
  const [row, setRow] = useState<ContentRow | null>(null);
  const [draftData, setDraftData] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const open = async () => {
    setError('');
    const rows = await api<ContentRow[]>(`/content/${type}`);
    const current = rows[0];
    if (!current) {
      setError('模块二维码配置尚未初始化，请先运行数据库迁移。');
      return;
    }
    setRow(current);
    setDraftData(prepareContentForEditor(type, current.data));
  };
  return (
    <>
      <Button onClick={() => void open()}>{label}</Button>
      {error && <Alert type="error" showIcon title={error} />}
      <Drawer
        title={title ?? contentLabels[type]}
        width={760}
        open={!!row}
        onClose={() => {
          setRow(null);
          setDraftData(null);
        }}
        extra={
          <Button
            type="primary"
            loading={saving}
            onClick={async () => {
              if (!row) return;
              setSaving(true);
              try {
                await api(
                  `/content/${type}/${row.id}`,
                  json('PATCH', {
                    revision: row.revision,
                    data: sanitizeContentForSave(draftData),
                  }),
                );
                setRow(null);
                setDraftData(null);
              } finally {
                setSaving(false);
              }
            }}
          >
            保存
          </Button>
        }
      >
        <Alert type="info" showIcon title={description} />
        {draftData !== null && (
          <ContentEditor
            type={type}
            value={draftData}
            onChange={setDraftData}
            fieldKeys={fieldKeys}
            sectionTitle={title}
          />
        )}
      </Drawer>
    </>
  );
}

function PreviewPane({
  title,
  url,
  loading,
  onRefresh,
}: {
  title: string;
  url: string;
  loading: boolean;
  onRefresh: () => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const frameUrl = useMemo(() => {
    if (!url) return '';
    const preview = new URL(url);
    preview.searchParams.delete('draftVersion');
    return preview.toString();
  }, [url]);
  useEffect(() => {
    if (!url) return;
    const preview = new URL(url);
    const notifyFrame = () =>
      frameRef.current?.contentWindow?.postMessage(
        { type: 'luobo:preview-update', token: preview.searchParams.get('preview') },
        preview.origin,
      );
    const onMessage = (event: MessageEvent) => {
      if (
        event.source === frameRef.current?.contentWindow &&
        event.origin === preview.origin &&
        event.data?.type === 'luobo:preview-ready'
      )
        notifyFrame();
    };
    window.addEventListener('message', onMessage);
    notifyFrame();
    return () => window.removeEventListener('message', onMessage);
  }, [url]);
  const [mode, setMode] = useState<'desktop' | 'mobile'>('desktop');
  const [desktopScale, setDesktopScale] = useState(1);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const update = () =>
      setDesktopScale(Math.min(1, Math.max(0.1, (stage.clientWidth - 36) / 1440)));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);
  const width = mode === 'desktop' ? 1440 : 390;
  const scale = mode === 'desktop' ? desktopScale : 1;
  return (
    <aside className="content-preview-pane">
      <div className="content-preview-toolbar">
        <div>
          <strong>{title}</strong>
          <Text type="secondary">按真实页面宽度缩放显示</Text>
        </div>
        <Space>
          <Button
            size="small"
            type={mode === 'desktop' ? 'primary' : 'default'}
            onClick={() => setMode('desktop')}
          >
            电脑
          </Button>
          <Button
            size="small"
            type={mode === 'mobile' ? 'primary' : 'default'}
            onClick={() => setMode('mobile')}
          >
            手机
          </Button>
          <Button size="small" loading={loading} onClick={onRefresh}>
            刷新
          </Button>
        </Space>
      </div>
      <div className={`content-preview-stage ${mode}`} ref={stageRef}>
        {loading && !url ? (
          <Spin />
        ) : url ? (
          <div
            className="content-preview-canvas"
            style={{ width: width * scale, height: 900 * scale }}
          >
            <iframe
              ref={frameRef}
              src={frameUrl}
              title={title}
              style={{ width, height: 900, transform: `scale(${scale})` }}
            />
          </div>
        ) : (
          <Text type="secondary">预览暂不可用，请稍后刷新。</Text>
        )}
      </div>
    </aside>
  );
}

function HomeModules({
  onNavigate,
  canAccess,
}: {
  onNavigate: (key: string) => void;
  canAccess: (key: string) => boolean;
}) {
  const route = useAdminRoute();
  const [row, setRow] = useState<ContentRow | null>(null);
  const [editingModule, setEditingModule] = useState<HomeModule | null>(null);
  const [draftData, setDraftData] = useState<unknown>(null);
  const [baseline, setBaseline] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [teacherRows, setTeacherRows] = useState<ContentRow[]>([]);
  const [teacherEditing, setTeacherEditing] = useState<ContentRow | null>(null);
  const [teacherCreating, setTeacherCreating] = useState(false);
  useUnsavedEditor(!!editingModule && JSON.stringify(draftData) !== baseline);
  const load = () => {
    setLoading(true);
    api<ContentRow[]>('/content/home')
      .then((rows) => {
        setRow(rows[0] ?? null);
        setError(rows[0] ? '' : '首页内容尚未初始化，请先运行数据库迁移。');
      })
      .catch((loadError) =>
        setError(loadError instanceof Error ? loadError.message : '首页内容加载失败'),
      )
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const refreshPreview = async (
    module: HomeModule,
    currentRow = row,
    previewData: unknown = draftData,
  ) => {
    if (!currentRow) return;
    setPreviewLoading(true);
    try {
      const result = await api<{ url: string }>(
        `/content/home/${currentRow.id}/preview`,
        json('POST', { data: sanitizeContentForSave(previewData ?? currentRow.data) }),
      );
      setPreviewUrl(embeddedPreviewUrl(result.url, 'home', module.anchor));
    } finally {
      setPreviewLoading(false);
    }
  };
  const refreshTeacherPreview = async (
    module: HomeModule,
    teacher: ContentRow,
    previewData: unknown = draftData,
  ) => {
    setPreviewLoading(true);
    try {
      const result = await api<{ url: string }>(
        `/content/teacher/${teacher.id}/preview`,
        json('POST', { data: sanitizeContentForSave(previewData ?? teacher.data) }),
      );
      setPreviewUrl(embeddedPreviewUrl(result.url, 'teacher', module.anchor));
    } finally {
      setPreviewLoading(false);
    }
  };
  const selectTeacher = (module: HomeModule, teacher: ContentRow) => {
    const prepared = prepareContentForEditor('teacher', teacher.data);
    setTeacherEditing(teacher);
    setTeacherCreating(false);
    setDraftData(prepared);
    setBaseline(JSON.stringify(prepared));
    setPreviewUrl('');
    void refreshTeacherPreview(module, teacher, prepared);
  };
  const reloadTeachers = async (selectedId?: string) => {
    const teachers = await api<ContentRow[]>('/content/teacher');
    setTeacherRows(teachers);
    if (selectedId) {
      const selected = teachers.find((teacher) => teacher.id === selectedId);
      if (selected) setTeacherEditing(selected);
    }
    return teachers;
  };
  const openModule = async (module: HomeModule) => {
    if (!navigateAdmin('home', module.key)) return;
    if (module.editorType === 'teachers') {
      setEditingModule(module);
      setError('');
      setPreviewUrl('');
      try {
        const teachers = await api<ContentRow[]>('/content/teacher');
        setTeacherRows(teachers);
        if (teachers[0]) selectTeacher(module, teachers[0]);
        else {
          setTeacherEditing(null);
          setDraftData(null);
          setBaseline('');
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : '师资内容加载失败');
      }
      return;
    }
    if (!row || !module.fieldKeys) return;
    const prepared = prepareContentForEditor('home', row.data);
    setEditingModule(module);
    setDraftData(prepared);
    setBaseline(JSON.stringify(prepared));
    setPreviewUrl('');
    setError('');
    void refreshPreview(module, row, prepared);
  };
  const close = () => {
    if (!navigateAdmin('home')) return;
    setEditingModule(null);
    setDraftData(null);
    setPreviewUrl('');
    setTeacherRows([]);
    setTeacherEditing(null);
    setTeacherCreating(false);
  };
  useEffect(() => {
    if (!route.editor && editingModule) {
      setEditingModule(null);
      setDraftData(null);
      setPreviewUrl('');
      setTeacherRows([]);
      setTeacherEditing(null);
      setTeacherCreating(false);
      return;
    }
    if (!route.editor || loading || !row || editingModule?.key === route.editor) return;
    const module = [...homeModules, homeQrModule].find((item) => item.key === route.editor);
    if (module) void openModule(module);
  }, [route.editor, loading, row, editingModule?.key]);
  const save = async () => {
    if (!editingModule) return;
    setSaving(true);
    setError('');
    try {
      if (editingModule.editorType === 'teachers') {
        const saved = teacherCreating
          ? await api<ContentRow>(
              '/content/teacher',
              json('POST', {
                key: resourceKeyFor('teacher', draftData),
                data: sanitizeContentForSave(draftData),
              }),
            )
          : await api<ContentRow>(
              `/content/teacher/${teacherEditing?.id}`,
              json('PATCH', {
                revision: teacherEditing?.revision,
                data: sanitizeContentForSave(draftData),
              }),
            );
        const teachers = await api<ContentRow[]>('/content/teacher');
        setTeacherRows(teachers);
        const prepared = prepareContentForEditor('teacher', saved.data);
        setTeacherEditing(saved);
        setTeacherCreating(false);
        setDraftData(prepared);
        setBaseline(JSON.stringify(prepared));
        await refreshTeacherPreview(editingModule, saved, prepared);
        return;
      }
      if (!row) return;
      const updated = await api<ContentRow>(
        `/content/home/${row.id}`,
        json('PATCH', { revision: row.revision, data: sanitizeContentForSave(draftData) }),
      );
      const prepared = prepareContentForEditor('home', updated.data);
      setRow(updated);
      setDraftData(prepared);
      setBaseline(JSON.stringify(prepared));
      await refreshPreview(editingModule, updated, prepared);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '保存失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      {!editingModule && (
        <>
          <div className="page-title">
            <div>
              <Title level={2}>首页内容</Title>
              <Text type="secondary">按照官网从上到下的顺序，逐个模块独立配置和预览。</Text>
            </div>
            <Button disabled={!canAccess('home')} onClick={() => void openModule(homeQrModule)}>
              配置首页兜底二维码
            </Button>
          </div>
          {error && !editingModule && <Alert type="error" showIcon title={error} />}
          <Table
            rowKey="key"
            loading={loading}
            dataSource={homeModules}
            pagination={false}
            columns={[
              { title: '页面顺序', render: (_, __, index) => index + 1, width: 90 },
              { title: '首页模块', dataIndex: 'title', width: 190 },
              { title: '内容说明', dataIndex: 'description' },
              {
                title: '管理位置',
                render: (_, module) =>
                  module.navigateTo ? (
                    <Tag color="blue">引用{contentLabels[module.navigateTo]}</Tag>
                  ) : (
                    <Tag color="green">首页独立配置</Tag>
                  ),
                width: 180,
              },
              {
                title: '操作',
                render: (_, module) =>
                  module.navigateTo ? (
                    <Button
                      type="primary"
                      disabled={!canAccess(module.navigateTo)}
                      onClick={() => onNavigate(module.navigateTo!)}
                    >
                      管理{contentLabels[module.navigateTo]}
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      disabled={!canAccess(module.editorType === 'teachers' ? 'teacher' : 'home')}
                      onClick={() => void openModule(module)}
                    >
                      编辑与预览
                    </Button>
                  ),
                width: 170,
              },
            ]}
          />
        </>
      )}
      {editingModule && (
        <EditorPage
          title={`编辑首页模块 · ${editingModule?.title ?? ''}`}
          onBack={close}
          actions={
            <Space>
              <Button disabled={!previewUrl} onClick={() => previewUrl && window.open(previewUrl)}>
                新窗口查看
              </Button>
              <Button
                type="primary"
                loading={saving}
                disabled={editingModule?.editorType === 'teachers' && draftData === null}
                onClick={save}
              >
                保存并刷新预览
              </Button>
            </Space>
          }
        >
          <EditorWorkspace>
            <section className="content-edit-pane">
              <Alert
                type="info"
                showIcon
                title="当前只编辑这个首页模块；保存后右侧预览更新，正式官网仍需发布版本。"
              />
              {error && <Alert type="error" showIcon title="暂时无法保存" description={error} />}
              {editingModule?.editorType === 'teachers' && (
                <Card
                  className="teacher-module-editor"
                  title="首页师资成员"
                  extra={
                    <Space wrap>
                      <ModuleQrSettingsButton
                        type="home"
                        label="配置师资统一二维码"
                        title="师资统一二维码"
                        fieldKeys={['qrCodes.teacher.image']}
                        description="未给老师设置独立二维码时，预约老师弹窗使用这里的图片。弹窗标题和说明统一在“全局设置”中维护。"
                      />
                      <Button
                        type="primary"
                        onClick={() => {
                          const prepared = prepareContentForEditor(
                            'teacher',
                            createContentDefault('teacher'),
                          );
                          setTeacherCreating(true);
                          setTeacherEditing(null);
                          setDraftData(prepared);
                          setBaseline(JSON.stringify(prepared));
                          setPreviewUrl('');
                        }}
                      >
                        新增老师
                      </Button>
                    </Space>
                  }
                >
                  <Space direction="vertical" size="middle" className="teacher-module-controls">
                    <div className="teacher-member-list" aria-label="已配置老师列表">
                      {teacherRows.map((teacher) => {
                        const teacherData = teacher.data as Record<string, unknown>;
                        const selected = teacherEditing?.id === teacher.id;
                        return (
                          <button
                            type="button"
                            className={`teacher-member-card${selected ? ' selected' : ''}`}
                            key={teacher.id}
                            onClick={() => editingModule && selectTeacher(editingModule, teacher)}
                          >
                            <span className="teacher-member-image">
                              {typeof teacherData.image === 'string' && teacherData.image ? (
                                <img src={teacherData.image} alt="" />
                              ) : (
                                <UserOutlined />
                              )}
                            </span>
                            <strong>
                              {contentDisplayName(teacher.data, teacher.resource_key)}
                            </strong>
                            <small>
                              {teacherData.lead ? '领衔老师 · ' : ''}
                              {teacher.state === 'published'
                                ? '上架'
                                : teacher.state === 'draft'
                                  ? '草稿'
                                  : '下架'}
                            </small>
                          </button>
                        );
                      })}
                    </div>
                    {teacherCreating && (
                      <Alert
                        type="info"
                        showIcon
                        title="正在新增老师"
                        description="填写下方老师资料后，点击右上角“保存并刷新预览”。"
                      />
                    )}
                    {teacherEditing && (
                      <Space wrap>
                        <Button
                          onClick={async () => {
                            const updated = await api<ContentRow>(
                              `/content/teacher/${teacherEditing.id}`,
                              json('PATCH', {
                                revision: teacherEditing.revision,
                                state:
                                  teacherEditing.state === 'published' ? 'offline' : 'published',
                              }),
                            );
                            setTeacherEditing(updated);
                            setTeacherRows((items) =>
                              items.map((item) => (item.id === updated.id ? updated : item)),
                            );
                          }}
                        >
                          {teacherEditing.state === 'published' ? '设为下架' : '设为上架'}
                        </Button>
                        <Button
                          disabled={
                            teacherRows.findIndex((item) => item.id === teacherEditing.id) === 0
                          }
                          onClick={async () => {
                            const index = teacherRows.findIndex(
                              (item) => item.id === teacherEditing.id,
                            );
                            const reordered = [...teacherRows];
                            [reordered[index - 1], reordered[index]] = [
                              reordered[index]!,
                              reordered[index - 1]!,
                            ];
                            await api(
                              '/content/teacher/reorder',
                              json('POST', {
                                items: reordered.map((item, itemIndex) => ({
                                  id: item.id,
                                  revision: item.revision,
                                  sort: itemIndex + 1,
                                })),
                              }),
                            );
                            await reloadTeachers(teacherEditing.id);
                          }}
                        >
                          上移
                        </Button>
                        <Button
                          disabled={
                            teacherRows.findIndex((item) => item.id === teacherEditing.id) ===
                            teacherRows.length - 1
                          }
                          onClick={async () => {
                            const index = teacherRows.findIndex(
                              (item) => item.id === teacherEditing.id,
                            );
                            const reordered = [...teacherRows];
                            [reordered[index], reordered[index + 1]] = [
                              reordered[index + 1]!,
                              reordered[index]!,
                            ];
                            await api(
                              '/content/teacher/reorder',
                              json('POST', {
                                items: reordered.map((item, itemIndex) => ({
                                  id: item.id,
                                  revision: item.revision,
                                  sort: itemIndex + 1,
                                })),
                              }),
                            );
                            await reloadTeachers(teacherEditing.id);
                          }}
                        >
                          下移
                        </Button>
                        <Popconfirm
                          title="确认删除这位老师？30 天内可在回收站恢复。"
                          onConfirm={async () => {
                            await api(`/content/teacher/${teacherEditing.id}`, {
                              method: 'DELETE',
                            });
                            const teachers = await reloadTeachers();
                            if (teachers[0] && editingModule)
                              selectTeacher(editingModule, teachers[0]);
                            else {
                              setTeacherEditing(null);
                              setDraftData(null);
                              setPreviewUrl('');
                            }
                          }}
                        >
                          <Button danger>删除</Button>
                        </Popconfirm>
                      </Space>
                    )}
                  </Space>
                  {draftData !== null && (
                    <ContentEditor
                      type="teacher"
                      value={draftData}
                      onChange={setDraftData}
                      imageOrigin={previewUrl ? new URL(previewUrl).origin : undefined}
                    />
                  )}
                </Card>
              )}
              {draftData !== null && editingModule?.fieldKeys && (
                <ContentEditor
                  type="home"
                  value={draftData}
                  onChange={setDraftData}
                  fieldKeys={editingModule.fieldKeys}
                  sectionTitle={editingModule.title}
                />
              )}
            </section>
            <PreviewPane
              title={`${editingModule?.title ?? '首页模块'}预览`}
              url={previewUrl}
              loading={previewLoading}
              onRefresh={() => {
                if (!editingModule) return;
                if (editingModule.editorType === 'teachers' && teacherEditing)
                  void refreshTeacherPreview(editingModule, teacherEditing, draftData);
                else void refreshPreview(editingModule, row, draftData);
              }}
            />
          </EditorWorkspace>
        </EditorPage>
      )}
    </>
  );
}

function ContentManager({
  type,
  settingsType,
}: {
  type: ContentType;
  settingsType?: Extract<
    ContentType,
    'courseSettings' | 'materialSettings' | 'caseSettings' | 'teacherSettings'
  >;
}) {
  const [rows, setRows] = useState<ContentRow[]>([]);
  const route = useAdminRoute();
  const [editing, setEditing] = useState<ContentRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [draftData, setDraftData] = useState<unknown>(null);
  const [baseline, setBaseline] = useState('');
  const [editorError, setEditorError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchUpdating, setBatchUpdating] = useState(false);
  const batchPublishIds = batchStateIds(rows, selectedIds, 'published');
  const batchOfflineIds = batchStateIds(rows, selectedIds, 'offline');
  const [showDeleted, setShowDeleted] = useState(false);
  const [versionRow, setVersionRow] = useState<ContentRow | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [dragged, setDragged] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTokenRef = useRef('');
  const previewRequestRef = useRef(0);
  const previewVersionRef = useRef(0);
  const previewDataRef = useRef('');
  const [previewError, setPreviewError] = useState('');
  useUnsavedEditor(!!editing && JSON.stringify(draftData) !== baseline);
  const singletons = new Set<ContentType>(['home', 'about', 'navigation', 'footer', 'brand']);
  const singleton = singletons.has(type);
  const load = () => {
    setLoading(true);
    api<ContentRow[]>(`/content/${type}${showDeleted ? '?deleted=true' : ''}`)
      .then(setRows)
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, [type, showDeleted]);
  const refreshPreview = async (
    row: ContentRow,
    previewData: unknown = draftData,
    silent = false,
  ) => {
    const requestId = ++previewRequestRef.current;
    setPreviewLoading(true);
    try {
      const result = await api<{ url: string; token: string }>(
        `/content/${type}/${row.id}/preview`,
        json('POST', {
          data: sanitizeContentForSave(previewData ?? row.data),
          token: previewTokenRef.current || undefined,
        }),
      );
      if (requestId !== previewRequestRef.current) return;
      previewTokenRef.current = result.token;
      previewDataRef.current = JSON.stringify(previewData ?? row.data);
      previewVersionRef.current += 1;
      setPreviewError('');
      setPreviewUrl(embeddedPreviewUrl(result.url, type, undefined, previewVersionRef.current));
    } catch (error) {
      if (requestId === previewRequestRef.current)
        setPreviewError(
          silent
            ? '部分内容尚未填写完整，预览暂时保留上一次有效内容。'
            : error instanceof Error
              ? error.message
              : '预览更新失败',
        );
    } finally {
      if (requestId === previewRequestRef.current) setPreviewLoading(false);
    }
  };
  const open = (row: ContentRow) => {
    if (!navigateAdmin(type, row.id)) return;
    const prepared = prepareContentForEditor(type, row.data);
    if (prepared && typeof prepared === 'object' && !Array.isArray(prepared)) {
      const keyField =
        type === 'course' ? 'code' : type === 'material' ? 'slug' : type === 'case' ? 'id' : null;
      if (keyField) (prepared as Record<string, unknown>)[keyField] = row.resource_key;
    }
    setEditing(row);
    setDraftData(prepared);
    setBaseline(JSON.stringify(prepared));
    setEditorError('');
    setPreviewUrl('');
    previewTokenRef.current = '';
    previewRequestRef.current += 1;
    previewVersionRef.current = 0;
    previewDataRef.current = '';
    setPreviewError('');
    void refreshPreview(row, prepared);
  };
  const closeEditor = () => {
    if (!navigateAdmin(type)) return;
    setEditing(null);
    setDraftData(null);
    setEditorError('');
    setPreviewUrl('');
    previewTokenRef.current = '';
    previewRequestRef.current += 1;
    previewDataRef.current = '';
    setPreviewError('');
  };
  useEffect(() => {
    if (!route.editor && editing) {
      setEditing(null);
      setDraftData(null);
      setPreviewUrl('');
      previewRequestRef.current += 1;
      return;
    }
    if (!route.editor || editing?.id === route.editor) return;
    const row = rows.find((item) => item.id === route.editor);
    if (row) open(row);
  }, [route.editor, rows, editing?.id]);
  const closeCreate = () => {
    if (
      creating &&
      JSON.stringify(draftData) !== baseline &&
      !window.confirm('新建内容尚未保存，确定关闭吗？')
    )
      return;
    setCreating(false);
    setDraftData(null);
    setEditorError('');
  };
  useEffect(() => {
    if (!editing || draftData === null || JSON.stringify(draftData) === previewDataRef.current)
      return;
    const timer = window.setTimeout(() => {
      void refreshPreview(editing, draftData, true);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [baseline, draftData, editing, type]);
  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setEditorError('');
    try {
      const updated = await api<ContentRow>(
        `/content/${type}/${editing.id}`,
        json('PATCH', { revision: editing.revision, data: sanitizeContentForSave(draftData) }),
      );
      const prepared = prepareContentForEditor(type, updated.data);
      setEditing(updated);
      setDraftData(prepared);
      setBaseline(JSON.stringify(prepared));
      await refreshPreview(updated, prepared);
      load();
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : '保存失败，请检查填写内容');
    } finally {
      setSaving(false);
    }
  };
  const publishState = async (row: ContentRow, state: string) => {
    await api(`/content/${type}/${row.id}`, json('PATCH', { revision: row.revision, state }));
    load();
  };
  const batchState = async (state: 'published' | 'offline') => {
    const ids = batchStateIds(rows, selectedIds, state);
    if (!ids.length || showDeleted || batchUpdating || loading) return;
    setBatchUpdating(true);
    try {
      await api(`/content/${type}/batch-state`, json('POST', { ids, state }));
      setSelectedIds([]);
      load();
    } finally {
      setBatchUpdating(false);
    }
  };
  const openVersions = async (row: ContentRow) => {
    setVersionRow(row);
    setVersions(await api<VersionRow[]>(`/content/${type}/${row.id}/versions`));
  };
  const dropOn = async (targetId: string) => {
    if (!dragged || dragged === targetId) return;
    const reordered = [...rows];
    const from = reordered.findIndex((item) => item.id === dragged);
    const to = reordered.findIndex((item) => item.id === targetId);
    const [moved] = reordered.splice(from, 1);
    if (!moved) return;
    reordered.splice(to, 0, moved);
    await api(
      `/content/${type}/reorder`,
      json('POST', {
        items: reordered.map((item, index) => ({
          id: item.id,
          revision: item.revision,
          sort: index + 1,
        })),
      }),
    );
    setDragged(null);
    load();
  };
  return (
    <>
      {!editing && (
        <>
          <div className="page-title">
            <div>
              <Title level={2}>{contentLabels[type]}</Title>
              {contentDescriptions[type] && (
                <Text type="secondary">{contentDescriptions[type]}</Text>
              )}
            </div>
            <Space wrap>
              {settingsType && (
                <ModuleQrSettingsButton
                  type={settingsType}
                  label={`配置${contentLabels[settingsType]}`}
                />
              )}
              {!singleton && (
                <>
                  <Button
                    disabled={!batchPublishIds.length || showDeleted || batchUpdating || loading}
                    onClick={() => batchState('published')}
                  >
                    批量设为上架
                  </Button>
                  <Button
                    disabled={!batchOfflineIds.length || showDeleted || batchUpdating || loading}
                    onClick={() => batchState('offline')}
                  >
                    批量设为下架
                  </Button>
                </>
              )}
              <Button onClick={() => setShowDeleted(!showDeleted)}>
                {showDeleted ? '返回当前内容' : '回收站'}
              </Button>
              {!singleton && (
                <Button
                  type="primary"
                  disabled={showDeleted}
                  onClick={() => {
                    const initial = prepareContentForEditor(type, createContentDefault(type));
                    setCreating(true);
                    setDraftData(initial);
                    setBaseline(JSON.stringify(initial));
                    setEditorError('');
                  }}
                >
                  新建{contentLabels[type]}
                </Button>
              )}
            </Space>
          </div>
          <Alert
            type="info"
            showIcon
            title="修改后，需要发布整站才会更新官网。"
            description="保存、设为上架或下架、删除和调整排序都不会立即影响官网。草稿不会随整站发布上线。"
            style={{ marginBottom: 16 }}
          />
          <Table
            rowKey="id"
            loading={loading}
            dataSource={rows}
            pagination={{ pageSize: 20 }}
            rowSelection={
              singleton
                ? undefined
                : {
                    selectedRowKeys: selectedIds,
                    onChange: (keys) => setSelectedIds(keys.map(String)),
                  }
            }
            onRow={(row) => ({
              draggable: !showDeleted && !singleton,
              onDragStart: () => setDragged(row.id),
              onDragOver: (event) => event.preventDefault(),
              onDrop: () => void dropOn(row.id),
            })}
            columns={[
              {
                title: type === 'teacher' ? '系统标识' : '页面网址',
                dataIndex: 'resource_key',
                render: (key) => {
                  if (type === 'teacher')
                    return (
                      <Space direction="vertical" size={0}>
                        <Text>首页师资区</Text>
                        <Text type="secondary">系统标识：{String(key)}</Text>
                      </Space>
                    );
                  const path = contentPagePath(type, String(key));
                  return path ? <Text code>{path}</Text> : <Text type="secondary">系统配置</Text>;
                },
              },
              {
                title: '名称 / 标题',
                render: (_, row) => {
                  const isLead =
                    type === 'teacher' &&
                    typeof row.data === 'object' &&
                    row.data !== null &&
                    'lead' in row.data &&
                    row.data.lead === true;
                  return (
                    <Space>
                      <Text>{contentDisplayName(row.data, row.resource_key)}</Text>
                      {isLead && <Tag color="purple">领衔老师</Tag>}
                    </Space>
                  );
                },
              },
              {
                title: '上架设置',
                dataIndex: 'state',
                render: (state) => (
                  <Tag
                    color={state === 'published' ? 'green' : state === 'draft' ? 'gold' : 'default'}
                  >
                    {state === 'published' ? '上架' : state === 'draft' ? '草稿' : '下架'}
                  </Tag>
                ),
              },
              { title: '排序', dataIndex: 'sort', width: 90 },
              { title: '版本号', dataIndex: 'revision', width: 90 },
              {
                title: '更新时间',
                dataIndex: 'updated_at',
                render: (value) => dayjs(value).format('YYYY-MM-DD HH:mm'),
              },
              {
                title: '操作',
                render: (_, row) => (
                  <Space>
                    {showDeleted ? (
                      <Button
                        size="small"
                        onClick={async () => {
                          await api(`/content/${type}/${row.id}/recover`, json('POST', {}));
                          load();
                        }}
                      >
                        恢复
                      </Button>
                    ) : (
                      <>
                        <Button size="small" type="primary" onClick={() => open(row)}>
                          编辑与预览
                        </Button>
                        <Button
                          size="small"
                          onClick={() =>
                            publishState(row, row.state === 'published' ? 'offline' : 'published')
                          }
                        >
                          {row.state === 'published' ? '设为下架' : '设为上架'}
                        </Button>
                        <Button size="small" onClick={() => void openVersions(row)}>
                          版本
                        </Button>
                        {!singleton && (
                          <Popconfirm
                            title="30 天内可恢复，确认删除？"
                            onConfirm={async () => {
                              await api(`/content/${type}/${row.id}`, { method: 'DELETE' });
                              load();
                            }}
                          >
                            <Button danger size="small">
                              删除
                            </Button>
                          </Popconfirm>
                        )}
                      </>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </>
      )}
      {editing && (
        <EditorPage
          title={
            <Space>
              <span>编辑{contentLabels[type]}</span>
              {editing && contentPagePath(type, editing.resource_key) && (
                <Text code>{contentPagePath(type, editing.resource_key)}</Text>
              )}
            </Space>
          }
          onBack={closeEditor}
          actions={
            <Space>
              <Button disabled={!previewUrl} onClick={() => previewUrl && window.open(previewUrl)}>
                新窗口查看
              </Button>
              <Button type="primary" loading={saving} onClick={save}>
                保存修改
              </Button>
            </Space>
          }
        >
          <EditorWorkspace>
            <section className="content-edit-pane">
              <Alert
                type="info"
                showIcon
                title="左侧修改后，右侧预览会在停止输入约 0.7 秒后自动更新。保存不会立即影响正式官网，仍需发布版本才会生效。"
              />
              {editorError && (
                <Alert type="error" showIcon title="暂时无法保存" description={editorError} />
              )}
              {previewError && <Alert type="warning" showIcon title={previewError} />}
              {draftData !== null && (
                <ContentEditor
                  type={type}
                  value={draftData}
                  onChange={setDraftData}
                  imageOrigin={
                    type === 'teacher' && previewUrl ? new URL(previewUrl).origin : undefined
                  }
                  lockedFields={
                    type === 'course'
                      ? ['code']
                      : type === 'material'
                        ? ['slug']
                        : type === 'case'
                          ? ['id']
                          : []
                  }
                />
              )}
            </section>
            <PreviewPane
              title={`${contentLabels[type]}预览`}
              url={previewUrl}
              loading={previewLoading}
              onRefresh={() => editing && void refreshPreview(editing, draftData)}
            />
          </EditorWorkspace>
        </EditorPage>
      )}
      <Drawer
        title={`新建${contentLabels[type]}`}
        open={creating}
        width={980}
        onClose={closeCreate}
        extra={
          <Button
            type="primary"
            loading={saving}
            onClick={async () => {
              const key = resourceKeyFor(type, draftData);
              if (!key) {
                setEditorError('请先填写页面地址或内部标识。');
                return;
              }
              setSaving(true);
              setEditorError('');
              try {
                const created = await api<ContentRow>(
                  `/content/${type}`,
                  json('POST', { key, data: sanitizeContentForSave(draftData) }),
                );
                setCreating(false);
                open(created);
                load();
              } catch (error) {
                setEditorError(error instanceof Error ? error.message : '创建失败，请检查填写内容');
              } finally {
                setSaving(false);
              }
            }}
          >
            创建草稿
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          title="页面地址字段仅允许小写字母、数字和短横线；创建后建议不要修改。"
        />
        {editorError && (
          <Alert type="error" showIcon title="暂时无法创建" description={editorError} />
        )}
        {draftData !== null && (
          <ContentEditor type={type} value={draftData} onChange={setDraftData} />
        )}
      </Drawer>
      <Drawer
        title={`版本记录 · ${versionRow?.resource_key ?? ''}`}
        width={720}
        open={!!versionRow}
        onClose={() => setVersionRow(null)}
      >
        <Table
          rowKey="id"
          dataSource={versions}
          columns={[
            { title: '版本', dataIndex: 'version' },
            { title: '状态', dataIndex: 'state' },
            {
              title: '时间',
              dataIndex: 'created_at',
              render: (value) => dayjs(String(value)).format('YYYY-MM-DD HH:mm:ss'),
            },
            {
              title: '操作',
              render: (_, version) => (
                <Button
                  size="small"
                  onClick={async () => {
                    if (!versionRow) return;
                    await api(
                      `/content/${type}/${versionRow.id}/restore`,
                      json('POST', { version: version.version, revision: versionRow.revision }),
                    );
                    setVersionRow(null);
                    load();
                  }}
                >
                  恢复为新草稿
                </Button>
              ),
            },
          ]}
        />
      </Drawer>
    </>
  );
}

function Releases() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [pending, setPending] = useState<{
    items: {
      id: string;
      module: string;
      title: string;
      changes: string[];
      updatedAt: string | null;
      updatedBy: string;
    }[];
    checksum: string;
    hasPendingRelease: boolean;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { modal, message } = AntApp.useApp();
  const load = useCallback(async () => {
    const [releases, changes] = await Promise.all([
      api<Record<string, unknown>[]>('/releases'),
      api<NonNullable<typeof pending>>('/releases/pending'),
    ]);
    setRows(releases);
    setPending(changes);
  }, []);
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await load();
      setError('');
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : '加载发布信息失败');
    } finally {
      setLoading(false);
    }
  }, [load]);
  const hasPendingRelease =
    pending?.hasPendingRelease ||
    rows.some((row) =>
      ['queued', 'building', 'deploying'].includes(String(row.job_status ?? row.status)),
    );
  useEffect(() => {
    void refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);
  useEffect(() => {
    if (!hasPendingRelease) return;
    const timer = window.setInterval(() => void refresh(), 1500);
    return () => window.clearInterval(timer);
  }, [hasPendingRelease, refresh]);
  const confirmPublish = () => {
    if (!pending || hasPendingRelease) return;
    modal.confirm({
      title: '确认发布整站？',
      content: `本次将发布全部 ${pending.items.length} 项待发布修改，不仅是某一条课程或案例。请确认内容已检查完成。`,
      okText: '确认发布整站',
      cancelText: '返回检查',
      onOk: async () => {
        setSubmitting(true);
        setError('');
        try {
          await api('/releases/publish', json('POST', { expectedChecksum: pending.checksum }));
          await load();
          void message.success('整站发布任务已提交，请等待发布成功后检查官网');
        } catch (failure) {
          setError(failure instanceof Error ? failure.message : '提交发布失败');
          await refresh();
          setError(failure instanceof Error ? failure.message : '提交发布失败');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };
  return (
    <>
      <div className="page-title">
        <Title level={2}>发布任务与版本</Title>
        <Space>
          <Button loading={loading} onClick={() => void refresh()}>
            刷新清单
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={submitting || hasPendingRelease}
            disabled={!pending || loading || hasPendingRelease}
            onClick={confirmPublish}
          >
            {hasPendingRelease ? '发布中' : '发布整站'}
          </Button>
        </Space>
      </div>
      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}
      <Alert
        type="info"
        showIcon
        title="各模块修改后统一在这里发布整站。"
        description={
          hasPendingRelease
            ? '发布进行中，请勿重复提交。此时清单仍与上次成功发布的版本比较；本次提交之后保存的新修改会留到下一次发布。'
            : '清单与最近一次成功发布的内容比较。草稿不会上线；发布失败不会清除待发布修改。'
        }
        style={{ marginBottom: 16 }}
      />
      <Card title={`待发布修改（${pending?.items.length ?? 0}）`} style={{ marginBottom: 24 }}>
        <Table
          rowKey="id"
          loading={loading && !pending}
          dataSource={pending?.items ?? []}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: '没有待发布修改（草稿不计入清单）' }}
          columns={[
            { title: '模块', dataIndex: 'module' },
            { title: '名称 / 标题', dataIndex: 'title' },
            {
              title: '修改内容',
              dataIndex: 'changes',
              render: (changes: string[]) => (
                <Space wrap>
                  {changes.map((change) => (
                    <Tag key={change}>{change}</Tag>
                  ))}
                </Space>
              ),
            },
            { title: '修改人', dataIndex: 'updatedBy' },
            {
              title: '修改时间',
              dataIndex: 'updatedAt',
              render: (value) => (value ? dayjs(String(value)).format('YYYY-MM-DD HH:mm') : '—'),
            },
          ]}
        />
      </Card>
      <Title level={3}>发布记录</Title>
      <Table
        rowKey="id"
        dataSource={rows}
        scroll={{ x: 1100 }}
        columns={[
          { title: 'Release ID', dataIndex: 'id' },
          { title: '操作人', dataIndex: 'created_by_name' },
          {
            title: '提交时间',
            dataIndex: 'created_at',
            render: (value) => dayjs(String(value)).format('YYYY-MM-DD HH:mm:ss'),
          },
          {
            title: '发布状态',
            dataIndex: 'status',
            render: (value) => statusTag(value, releaseStatuses),
          },
          {
            title: '任务状态',
            dataIndex: 'job_status',
            render: (value) => statusTag(value, releaseJobStatuses),
          },
          { title: '尝试', dataIndex: 'attempt' },
          {
            title: '激活时间',
            dataIndex: 'activated_at',
            render: (v) => (v ? dayjs(String(v)).format('YYYY-MM-DD HH:mm:ss') : '-'),
          },
          {
            title: '错误',
            dataIndex: 'last_error',
            render: (value) => (value ? String(value) : '-'),
          },
          {
            title: '操作',
            render: (_, row) =>
              row.job_status === 'failed' ? (
                <Button
                  disabled={hasPendingRelease || submitting}
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      await api(`/releases/${row.job_id}/retry`, json('POST', {}));
                      await load();
                      setError('');
                    } catch (failure) {
                      setError(failure instanceof Error ? failure.message : '重试失败');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  重试
                </Button>
              ) : (
                <Text type="secondary">无需操作</Text>
              ),
          },
        ]}
      />
    </>
  );
}

function Leads() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [contact, setContact] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [remark, setRemark] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [assignees, setAssignees] = useState<Record<string, unknown>[]>([]);
  const load = () => {
    const query = new URLSearchParams();
    if (contact) query.set('contact', contact);
    if (status) query.set('status', status);
    return api<Record<string, unknown>[]>(`/leads?${query}`).then(setRows);
  };
  useEffect(() => {
    void load();
  }, [contact, status]);
  useEffect(() => {
    void api<Record<string, unknown>[]>('/leads/assignees').then(setAssignees);
  }, []);
  return (
    <>
      <div className="page-title">
        <Title level={2}>线索</Title>
        <Space>
          <Input.Search
            allowClear
            placeholder="按手机号或微信号精确搜索"
            onSearch={setContact}
            style={{ width: 260 }}
          />
          <Select
            value={status}
            onChange={setStatus}
            style={{ width: 130 }}
            options={[
              { value: '', label: '全部状态' },
              { value: 'pending', label: '待联系' },
              { value: 'contacted', label: '已联系' },
              { value: 'converted', label: '已成单' },
              { value: 'invalid', label: '无效' },
            ]}
          />
          <Button
            onClick={() =>
              download('/leads/export/xlsx', `leads-${dayjs().format('YYYY-MM-DD')}.xlsx`)
            }
          >
            导出 Excel
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        dataSource={rows}
        columns={[
          { title: '称呼', dataIndex: 'name' },
          { title: '联系方式', dataIndex: 'contact_masked' },
          {
            title: '来源',
            dataIndex: 'source_slot',
            render: (value) => leadSourceDisplayName(String(value ?? '')),
          },
          { title: '目标分', dataIndex: 'goal_score', render: (value) => value || '-' },
          { title: '计划考试时间', dataIndex: 'exam_time', render: (value) => value || '-' },
          {
            title: '状态',
            dataIndex: 'status',
            render: (value, row) => (
              <Select
                size="small"
                value={String(value)}
                style={{ width: 100 }}
                onChange={async (next) => {
                  await api(`/leads/${row.id}`, json('PATCH', { status: next }));
                  await load();
                }}
                options={[
                  { value: 'pending', label: '待联系' },
                  { value: 'contacted', label: '已联系' },
                  { value: 'converted', label: '已成单' },
                  { value: 'invalid', label: '无效' },
                ]}
              />
            ),
          },
          { title: '跟进人', dataIndex: 'assignee_name', render: (value) => value || '-' },
          {
            title: '备注',
            dataIndex: 'remark',
            ellipsis: true,
            render: (value) => value || '-',
          },
          { title: '次数', dataIndex: 'submit_count' },
          {
            title: '最近提交',
            dataIndex: 'last_submitted_at',
            render: (v) => dayjs(String(v)).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: '操作',
            render: (_, row) => (
              <Space>
                <Button
                  size="small"
                  onClick={async () => {
                    const result = await api<{ contact: string }>(`/leads/${row.id}/contact`);
                    Modal.info({ title: '联系方式（本次查看已审计）', content: result.contact });
                  }}
                >
                  查看明文
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(row);
                    setRemark(String(row.remark ?? ''));
                    setAssigneeId(row.assignee_id ? String(row.assignee_id) : null);
                  }}
                >
                  跟进
                </Button>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={`线索跟进 · ${String(editing?.name ?? '')}`}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={async () => {
          if (!editing) return;
          await api(`/leads/${editing.id}`, json('PATCH', { remark, assigneeId }));
          setEditing(null);
          await load();
        }}
      >
        <Form layout="vertical">
          <Form.Item label="跟进人">
            <Select
              allowClear
              value={assigneeId}
              onChange={(value) => setAssigneeId(value ?? null)}
              options={assignees.map((item) => ({
                value: String(item.id),
                label: `${String(item.display_name)}（${String(item.username)}）`,
              }))}
            />
          </Form.Item>
          <Form.Item label="跟进备注">
            <Input.TextArea
              value={remark}
              onChange={(event) => setRemark(event.target.value)}
              maxLength={2000}
              showCount
              autoSize={{ minRows: 6 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function Accounts() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form] = Form.useForm();
  const load = () => api<Record<string, unknown>[]>('/accounts').then(setRows);
  useEffect(() => {
    void load();
  }, []);
  return (
    <>
      <Space className="page-title-row">
        <Title level={2}>账号权限</Title>
        <Button
          type="primary"
          onClick={() => {
            setEditing(null);
            form.resetFields();
            setOpen(true);
          }}
        >
          创建员工账号
        </Button>
      </Space>
      <Table
        rowKey="id"
        dataSource={rows}
        columns={[
          { title: '账号', dataIndex: 'username' },
          { title: '姓名', dataIndex: 'display_name' },
          { title: '超级管理员', dataIndex: 'is_super', render: (v) => (v ? '是' : '否') },
          {
            title: '首次改密',
            dataIndex: 'force_password_change',
            render: (v) => (v ? '待完成' : '已完成'),
          },
          {
            title: '权限',
            dataIndex: 'permissions',
            render: (v) =>
              Array.isArray(v)
                ? v.map((item) => permissionDisplayName(String(item))).join('、')
                : '',
          },
          { title: '启用', dataIndex: 'enabled', render: (v) => (v ? '是' : '否') },
          {
            title: '操作',
            render: (_, row) =>
              row.is_super ? null : (
                <Button
                  size="small"
                  onClick={() => {
                    setEditing(row);
                    form.setFieldsValue({
                      displayName: row.display_name,
                      permissions: row.permissions,
                      enabled: row.enabled,
                    });
                    setOpen(true);
                  }}
                >
                  编辑权限
                </Button>
              ),
          },
        ]}
      />
      <Modal
        title={editing ? '编辑员工账号' : '创建员工账号'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ permissions: [], enabled: true }}
          onFinish={async (values) => {
            if (editing) {
              await api(`/accounts/${editing.id}`, json('PATCH', values));
            } else {
              const result = await api<{ initialPassword: string }>(
                '/accounts',
                json('POST', values),
              );
              Modal.success({
                title: '账号已创建，请立即保存初始密码',
                content: <Typography.Text copyable>{result.initialPassword}</Typography.Text>,
              });
            }
            setOpen(false);
            await load();
          }}
        >
          {!editing && (
            <Form.Item label="账号" name="username" rules={[{ required: true }]}>
              <Input maxLength={50} showCount />
            </Form.Item>
          )}
          <Form.Item label="姓名" name="displayName" rules={[{ required: true }]}>
            <Input maxLength={50} showCount />
          </Form.Item>
          <Form.Item label="菜单权限" name="permissions">
            <Select mode="multiple" options={permissionOptions} optionFilterProp="label" />
          </Form.Item>
          {editing && (
            <Form.Item label="账号状态" name="enabled">
              <Select
                options={[
                  { value: true, label: '启用' },
                  { value: false, label: '停用' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}

function Audit() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  useEffect(() => {
    api<Record<string, unknown>[]>('/audit').then(setRows);
  }, []);
  return (
    <>
      <Title level={2}>操作日志</Title>
      <Table
        size="small"
        rowKey="id"
        dataSource={rows}
        pagination={{ pageSize: 30 }}
        columns={[
          {
            title: '时间',
            dataIndex: 'created_at',
            render: (v) => dayjs(String(v)).format('YYYY-MM-DD HH:mm:ss'),
          },
          { title: '操作者', dataIndex: 'actor_username' },
          { title: '动作', dataIndex: 'action' },
          { title: '资源', render: (_, r) => `${r.resource_type ?? ''} / ${r.resource_id ?? ''}` },
          {
            title: '变更',
            render: (_, r) => (
              <Button
                size="small"
                onClick={() =>
                  Modal.info({
                    width: 800,
                    title: '字段变更',
                    content: (
                      <pre className="audit-json">
                        {JSON.stringify({ before: r.before_value, after: r.after_value }, null, 2)}
                      </pre>
                    ),
                  })
                }
              >
                查看
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}

function Assets() {
  const [asset, setAsset] = useState<Record<string, unknown> | null>(null);
  const props: UploadProps = {
    accept: 'image/jpeg,image/png,image/webp',
    maxCount: 1,
    customRequest: async (options) => {
      try {
        const form = new FormData();
        form.append('file', options.file as File);
        const result = await api<Record<string, unknown>>('/assets/upload', {
          method: 'POST',
          body: form,
        });
        setAsset(result);
        options.onSuccess?.(result);
      } catch (error) {
        options.onError?.(error as Error);
      }
    },
  };
  return (
    <>
      <Title level={2}>图片处理工具（高级）</Title>
      <Alert
        showIcon
        title="普通内容请直接在课程、案例、资料或首页的图片字段上传。这里仅用于生成独立图片网址，供历史内容、临时素材或技术排查使用。"
        description="图片会自动校验、纠正方向、移除 EXIF 并转为 WebP；上传完成后会显示公开图片网址和文件信息。"
      />
      <Card className="upload-card">
        <Upload.Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <PictureOutlined />
          </p>
          <p>点击或拖拽 JPG、PNG、WebP，最大 15MB</p>
        </Upload.Dragger>
        {asset && (
          <Descriptions
            bordered
            column={1}
            items={Object.entries(asset).map(([key, value]) => ({
              key,
              label: key,
              children: String(value),
            }))}
          />
        )}
      </Card>
    </>
  );
}

function Shell({ user, onLogout }: { user: AdminUser; onLogout: () => void }) {
  const route = useAdminRoute();
  const selected = route.page;
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('luobo-sidebar-collapsed') === 'true',
  );
  const setSelected = (key: string) => {
    navigateAdmin(key);
  };
  const visibleMenuItems = useMemo(() => menusFor(user), [user]);
  const body = useMemo(() => {
    if (selected === 'dashboard') return <Dashboard />;
    if (selected === 'releases') return <Releases />;
    if (selected === 'leads') return <Leads />;
    if (selected === 'accounts') return <Accounts />;
    if (selected === 'audit') return <Audit />;
    if (selected === 'assets') return <Assets />;
    if (selected === 'home')
      return (
        <HomeModules
          onNavigate={setSelected}
          canAccess={(key) =>
            user.isSuper || user.permissions.includes('*') || user.permissions.includes(key)
          }
        />
      );
    if (selected === 'course')
      return <ContentManager key="course" type="course" settingsType="courseSettings" />;
    if (selected === 'material')
      return <ContentManager key="material" type="material" settingsType="materialSettings" />;
    if (selected === 'case')
      return <ContentManager key="case" type="case" settingsType="caseSettings" />;
    if (selected === 'teacher')
      return <ContentManager key="teacher" type="teacher" settingsType="teacherSettings" />;
    return <ContentManager key={selected} type={selected as ContentType} />;
  }, [selected, user]);
  return (
    <Layout className="app-shell">
      <Sider width={232} collapsedWidth={64} collapsed={collapsed} theme="dark">
        <div className={`admin-logo${collapsed ? ' is-collapsed' : ''}`}>
          {collapsed ? '🥕' : '🥕 萝卜雅思'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          items={visibleMenuItems}
          selectedKeys={[selected]}
          defaultOpenKeys={['content', 'operation', 'global', 'system']}
          onClick={({ key }) => setSelected(key)}
        />
      </Sider>
      <Layout>
        <Header className="admin-header">
          <Space>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              aria-label={collapsed ? '展开导航栏' : '收起导航栏'}
              aria-expanded={!collapsed}
              title={collapsed ? '展开导航栏' : '收起导航栏'}
              onClick={() => {
                setCollapsed(!collapsed);
                localStorage.setItem('luobo-sidebar-collapsed', String(!collapsed));
              }}
            />
            <Text>内容管理后台</Text>
          </Space>
          <Space>
            <UserOutlined />
            <Text>{user.displayName}</Text>
            <Button
              size="small"
              onClick={async () => {
                await api('/auth/logout', json('POST', {}));
                localStorage.removeItem('luobo-csrf');
                onLogout();
              }}
            >
              退出
            </Button>
          </Space>
        </Header>
        <Content className={`admin-content${route.editor ? ' has-editor' : ''}`}>{body}</Content>
      </Layout>
    </Layout>
  );
}

export function App() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    api<{ user: AdminUser }>('/auth/me')
      .then((result) => setUser(result.user))
      .catch(() => {
        localStorage.removeItem('luobo-csrf');
        setUser(null);
      })
      .finally(() => setReady(true));
  }, []);
  if (!ready)
    return (
      <div className="center">
        <Spin size="large" />
      </div>
    );
  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#ea722e', borderRadius: 8 } }}>
      <AntApp>
        {user ? (
          <>
            <Shell user={user} onLogout={() => setUser(null)} />
            {user.forcePasswordChange && (
              <PasswordReset
                user={user}
                onDone={() => setUser({ ...user, forcePasswordChange: false })}
              />
            )}
          </>
        ) : (
          <Login onLogin={setUser} />
        )}
      </AntApp>
    </ConfigProvider>
  );
}
